import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import Modal from '../../components/shared/Modal/Modal';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import Loading from '../../components/shared/Loading';
import {
  EvaluationTagDto,
  ManageResult,
  RequestStatus,
  RequestTag,
  SendOutcome,
  serviceEvaluations,
} from '../../services/service-evaluations';
import {
  EvaluationErrors,
  EvaluationLimits,
  EvaluationRecipientStatus,
  EvaluationRequestStatus,
} from '../../util/evaluationContract';
import { fromIsoDay, toIsoDay } from '../../util/bidRequestReminder';
import {
  defaultDueDay,
  isOwnRecipientOption,
  MissingLine,
  RecipientField,
  RecipientOption,
  TagPicker,
  toRecipientInputs,
} from '../app-evaluation-start';
import {
  errorCodeOf,
  ManageHandlers,
  notifyDone,
  notifySend,
  reportActionError,
  useManageAction,
} from './manageActions';
import { stockholmToday } from './reviewFormat';
import styles from './page-supplier-evaluations.module.scss';

type Props = ManageHandlers & { status: RequestStatus; onClose: () => void };

// A close that meets an utvärdering someone else already closed has done its job.
const closeOrConfirmClosed = async (requestId: string): Promise<ManageResult> => {
  try {
    return await serviceEvaluations.closeRequest(requestId);
  } catch (error) {
    if (errorCodeOf(error) !== EvaluationErrors.Closed) throw error;
    const next = await serviceEvaluations.getRequestStatus(requestId).catch(() => null);
    if (next?.request.status !== EvaluationRequestStatus.Closed) throw error;
    return { status: next, outcome: null };
  }
};

export const CloseRequestModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });
  const { waiting, failed, notSent } = status.tiles;
  const unanswered = waiting + failed + notSent;

  const close = () =>
    run(
      () => closeOrConfirmClosed(status.request._id),
      () => {
        notifyDone(t, t('status.close.done', 'Utvärderingen är stängd'), null);
        onClose();
      },
    );

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.close.title', 'Stäng utvärdering?')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.close.confirm', 'Håll in för att stänga'),
        action: close,
        holdToConfirm: true,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        {unanswered > 0 && (
          <p className={styles.answerText}>
            {t('status.close.unansweredLinks', {
              count: unanswered,
              defaultValue_one: '{{count}} mottagare har inte svarat. Länken slutar fungera.',
              defaultValue_other:
                '{{count}} mottagare har inte svarat. Deras länkar slutar fungera.',
            })}
          </p>
        )}
        <p className={styles.answerText}>
          {unanswered > 0
            ? t(
                'status.close.finalTextReopen',
                'Ingen kan svara efter att utvärderingen har stängts. Svar som redan har kommit in finns kvar. Du kan öppna den igen senare.',
              )
            : t(
                'status.close.finalTextNoReopen',
                'Ingen kan svara efter att utvärderingen har stängts. Svar som redan har kommit in finns kvar.',
              )}
        </p>
      </div>
    </Modal>
  );
};

export const ExtendDueDateModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });
  const current = status.request.dueDate;
  const [day, setDay] = useState(() => fromIsoDay(current));
  const dayKey = toIsoDay(day);
  const canSave = dayKey !== current && !saving;

  const save = () => {
    if (!canSave) return;
    run(
      () => serviceEvaluations.changeDueDate(status.request._id, dayKey),
      result => {
        notifyDone(
          t,
          t('status.changeDueDate.done', 'Sista svarsdagen är ändrad'),
          result.outcome,
          'dueDateNotice',
        );
        onClose();
      },
    );
  };

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.changeDueDate.title', 'Ändra sista svarsdag')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.changeDueDate.confirm', 'Spara'),
        action: save,
        disabled: !canSave,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <div className={styles.fieldGroup}>
          <DatePicker
            label={t('status.changeDueDate.label', 'Ny sista svarsdag')}
            selected={day}
            minDate={fromIsoDay(stockholmToday())}
            onChange={(next: Date | null) => next && setDay(next)}
          />
          <p className={`${styles.small} ${styles.muted}`}>
            {t(
              'status.changeDueDate.info',
              'Mottagare som inte har svarat får ett mejl med den nya sista svarsdagen.',
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export const ReopenModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const [dayError, setDayError] = useState<string | null>(null);
  const { saving, run } = useManageAction(handlers, {
    closeOnStateChange: onClose,
    showsOwnRefusal: error => {
      const code = errorCodeOf(error);
      if (code === EvaluationErrors.NoRecipients) {
        NotificationManager.error(
          t(
            'status.reopen.noRecipients',
            'Ingen mottagare kan svara längre, så utvärderingen kan inte öppnas igen.',
          ),
        );
        onClose();
        handlers.onStale();
        return true;
      }
      if (code !== EvaluationErrors.InvalidContext) return false;
      setDayError(t('status.reopen.invalidDay', 'Välj en sista svarsdag från och med i dag.'));
      return true;
    },
  });
  const current = status.request.dueDate;
  const todayKey = stockholmToday();
  const duePassed = current < todayKey;
  const [day, setDay] = useState(() =>
    fromIsoDay(duePassed ? defaultDueDay(fromIsoDay(todayKey)) : current),
  );
  const dayKey = toIsoDay(day);

  const pick = (next: Date | null) => {
    if (!next) return;
    setDay(next);
    setDayError(null);
  };

  const reopen = () => {
    const sendsDay = current < stockholmToday() || dayKey !== current;
    run(
      () =>
        sendsDay
          ? serviceEvaluations.reopenRequest(status.request._id, dayKey)
          : serviceEvaluations.reopenRequest(status.request._id),
      ({ revoked }) => {
        notifyDone(t, t('status.reopen.done', 'Utvärderingen är öppen igen'), null);
        if (revoked.duplicates) {
          NotificationManager.warning(
            t('status.reopen.revokedDuplicates', {
              count: revoked.duplicates,
              defaultValue_one:
                '{{count}} mottagare återkallades eftersom de redan har en öppen utvärdering av samma slag.',
              defaultValue_other:
                '{{count}} mottagare återkallades eftersom de redan har en öppen utvärdering av samma slag.',
            }),
          );
        }
        if (revoked.formerMembers) {
          NotificationManager.warning(
            t('status.reopen.revokedFormerMembers', {
              count: revoked.formerMembers,
              defaultValue_one:
                '{{count}} mottagare återkallades eftersom de inte längre finns i organisationen.',
              defaultValue_other:
                '{{count}} mottagare återkallades eftersom de inte längre finns i organisationen.',
            }),
          );
        }
        onClose();
      },
    );
  };

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.reopen.title', 'Öppna utvärderingen igen?')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.reopen.confirm', 'Öppna igen'),
        action: reopen,
        disabled: saving,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <p className={styles.answerText}>
          {t(
            'status.reopen.text',
            'Mottagare som inte har svarat kan svara via sina länkar igen. Inga mejl skickas. Använd Påminn väntande efteråt.',
          )}
        </p>
        <div className={styles.fieldGroup}>
          <DatePicker
            label={t('status.reopen.dueDate', 'Sista svarsdag')}
            selected={day}
            minDate={fromIsoDay(todayKey)}
            onChange={pick}
          />
          {dayError && (
            <p className={styles.fieldError} role="alert">
              {dayError}
            </p>
          )}
          {duePassed && (
            <p className={`${styles.small} ${styles.muted}`}>
              {t('status.reopen.duePassed', 'Den gamla sista svarsdagen har passerat. Välj en ny.')}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export const CancelRequestModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });

  const cancel = () =>
    run(
      () => serviceEvaluations.cancelRequest(status.request._id),
      () => {
        notifyDone(t, t('status.cancel.done', 'Utvärderingen är avbruten'), null);
        onClose();
      },
    );

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.cancel.title', 'Avbryt utvärdering?')}
      onCancelButtonClick={onClose}
      cancelButtonText={t('status.cancel.dismiss', 'Stäng')}
      buttonInfo={{
        label: t('status.cancel.confirm', 'Håll in för att avbryta'),
        action: cancel,
        holdToConfirm: true,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <p className={styles.answerText}>
          {t(
            'status.cancel.mistakeText',
            'Alla länkar slutar fungera och svaren räknas inte i leverantörens betyg. Svaren sparas men visas bara för administratörer. Använd det här för en utvärdering som skickades av misstag. Det går inte att ångra.',
          )}
        </p>
      </div>
    </Modal>
  );
};

export const liveRecipientsOf = (status: RequestStatus) =>
  status.recipients.filter(recipient => recipient.status !== EvaluationRecipientStatus.Revoked);

const emailOf = (value: string | null | undefined) => (value ?? '').toLowerCase();

const isSkipped = (option: RecipientOption, outcome: SendOutcome) =>
  outcome.skipped.some(skip => emailOf(skip.email) === emailOf(option.email));

export const AddRecipientsModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });
  const [picked, setPicked] = useState<RecipientOption[]>([]);
  const live = liveRecipientsOf(status);
  const existing = live.map(recipient => ({ userId: recipient.userId, email: recipient.email }));
  const room = Math.max(0, EvaluationLimits.MaxRecipientsPerRequest - live.length);
  const canSend = picked.length > 0 && !saving;

  const showOutcome = (outcome: SendOutcome, sent: RecipientOption[]) => {
    const onlyViewer = sent.length === 1 && isOwnRecipientOption(sent[0]);
    if (onlyViewer && !outcome.sent && !outcome.failed.length && !outcome.skipped.length) {
      NotificationManager.success(
        t('status.addRecipients.selfAdded', 'Du är tillagd. Svara med Svara nu.'),
      );
      return;
    }
    notifySend(t, outcome);
  };

  const send = () => {
    if (!canSend) return;
    const sending = picked;
    run(
      () => serviceEvaluations.addRecipients(status.request._id, toRecipientInputs(sending)),
      ({ outcome }) => {
        if (!outcome) {
          onClose();
          return;
        }
        showOutcome(outcome, sending);
        const kept = sending.filter(option => !isSkipped(option, outcome));
        if (kept.length) onClose();
        else setPicked(kept);
      },
    );
  };

  return (
    <Modal
      show
      setShow={onClose}
      title={t('status.addRecipients.title', 'Lägg till mottagare')}
      onCancelButtonClick={onClose}
      buttonInfo={{
        label: t('status.addRecipients.confirm', 'Lägg till och skicka'),
        action: send,
        disabled: !canSend,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        <div className={styles.fieldGroup}>
          <RecipientField
            value={picked}
            onChange={setPicked}
            max={room}
            existing={existing}
            noEmailFor={status.request.sender}
          />
          <MissingLine
            items={picked.length ? [] : [t('quick.missing.recipients', 'lägg till mottagare')]}
          />
        </div>
      </div>
    </Modal>
  );
};

// A tag whose doc is gone is missing from the org list but must stay removable.
const withRequestTags = (orgTags: EvaluationTagDto[], requestTags: RequestTag[]) => {
  const known = new Set(orgTags.map(tag => tag._id));
  return [...orgTags, ...requestTags.filter(tag => !known.has(tag._id))];
};

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

export const EditTagsModal: FC<Props> = ({ status, onClose, ...handlers }) => {
  const [t] = useTranslation('evaluations');
  const { saving, run } = useManageAction(handlers, { closeOnStateChange: onClose });
  const requestTags = status.request.tags;
  const initialIds = requestTags.map(tag => tag._id);
  const [tagIds, setTagIds] = useState(initialIds);
  const [tags, setTags] = useState<EvaluationTagDto[] | null>(null);
  const [orgTagsChanged, setOrgTagsChanged] = useState(false);
  const canSave = tags !== null && !saving && !sameIds(tagIds, initialIds);

  useEffect(() => {
    let current = true;
    serviceEvaluations.getTags().then(
      orgTags => current && setTags(withRequestTags(orgTags, requestTags)),
      error => {
        if (!current) return;
        reportActionError(t, error, handlers.onStale);
        onClose();
      },
    );
    return () => {
      current = false;
    };
  }, []);

  const close = () => {
    onClose();
    if (orgTagsChanged) handlers.onStale();
  };

  const changeOrgTags = (next: EvaluationTagDto[]) => {
    setTags(next);
    setOrgTagsChanged(true);
  };

  const save = () => {
    if (!canSave) return;
    run(
      () => serviceEvaluations.updateRequestTags(status.request._id, tagIds),
      () => {
        notifyDone(t, t('status.editTags.done', 'Taggarna är sparade'), null);
        onClose();
      },
    );
  };

  return (
    <Modal
      show
      setShow={close}
      title={t('status.editTags.title', 'Ändra taggar')}
      onCancelButtonClick={close}
      buttonInfo={{
        label: t('status.editTags.confirm', 'Spara'),
        action: save,
        disabled: !canSave,
        loaderShow: saving,
      }}
    >
      <div className={styles.modalBody}>
        {tags ? (
          <TagPicker
            value={tagIds}
            onChange={setTagIds}
            tags={tags}
            onTagsChange={changeOrgTags}
            keepArchivedIds={initialIds}
          />
        ) : (
          <Loading type="inline" />
        )}
      </div>
    </Modal>
  );
};
